import { call, put, takeEvery, takeLatest, all, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { inputsApi, CreateInputRequest, UpdateInputRequest } from '../../services/inputsApi';
import {
  setLoading,
  setBulkLoading,
  setError,
  setInputs,
  setSelectedInput,
  setInputTypes,
  addInput,
  updateInput,
  removeInput,
  setDialogOpen,
  setValidationErrors,
  setStatistics,
  deselectAllInputs,
  FETCH_INPUTS,
  FETCH_INPUT_BY_ID,
  CREATE_INPUT,
  UPDATE_INPUT,
  DELETE_INPUT,
  FETCH_INPUT_TYPES,
  BULK_ACTIVATE_INPUTS,
  BULK_DEACTIVATE_INPUTS,
  FETCH_INPUTS_STATISTICS,
} from '../reducers/inputsSlice';
import { RootState } from '../index';

// Worker Sagas
function* fetchInputsSaga(): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const inputs = yield call(inputsApi.getAll);
    yield put(setInputs(inputs));
  } catch (error: any) {
    console.error('Error fetching inputs:', error);
    yield put(setError(error.message || 'Failed to fetch inputs'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchInputByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const input = yield call(inputsApi.getById, action.payload);
    yield put(setSelectedInput(input));
  } catch (error: any) {
    console.error('Error fetching input:', error);
    yield put(setError(error.message || 'Failed to fetch input'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createInputSaga(action: PayloadAction<CreateInputRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the input
    const validationErrors = yield call(inputsApi.validation.validateBeforeSave, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newInput = yield call(inputsApi.create, action.payload);
    yield put(addInput(newInput));
    yield put(setDialogOpen(false));
    yield put(setValidationErrors([]));
    
    // Refresh statistics
    yield call(fetchInputsStatisticsSaga);
  } catch (error: any) {
    console.error('Error creating input:', error);
    yield put(setError(error.message || 'Failed to create input'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateInputSaga(action: PayloadAction<UpdateInputRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the input
    const validationErrors = yield call(inputsApi.validation.validateBeforeSave, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedInput = yield call(inputsApi.update, action.payload);
    yield put(updateInput(updatedInput));
    yield put(setDialogOpen(false));
    yield put(setValidationErrors([]));
    
    // Refresh statistics
    yield call(fetchInputsStatisticsSaga);
  } catch (error: any) {
    console.error('Error updating input:', error);
    yield put(setError(error.message || 'Failed to update input'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteInputSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    yield call(inputsApi.delete, action.payload);
    yield put(removeInput(action.payload));
    
    // Refresh statistics
    yield call(fetchInputsStatisticsSaga);
  } catch (error: any) {
    console.error('Error deleting input:', error);
    yield put(setError(error.message || 'Failed to delete input'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchInputTypesSaga(): Generator<any, void, any> {
  try {
    const inputTypes = yield call(inputsApi.getInputTypes);
    yield put(setInputTypes(inputTypes));
  } catch (error: any) {
    console.error('Error fetching input types:', error);
    yield put(setError(error.message || 'Failed to fetch input types'));
  }
}

function* bulkActivateInputsSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkLoading(true));
    yield put(setError(null));
    
    yield call(inputsApi.bulkActivate, action.payload);
    yield put(deselectAllInputs());
    
    // Refresh the inputs list
    yield call(fetchInputsSaga);
    yield call(fetchInputsStatisticsSaga);
  } catch (error: any) {
    console.error('Error bulk activating inputs:', error);
    yield put(setError(error.message || 'Failed to activate inputs'));
  } finally {
    yield put(setBulkLoading(false));
  }
}

function* bulkDeactivateInputsSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkLoading(true));
    yield put(setError(null));
    
    yield call(inputsApi.bulkDeactivate, action.payload);
    yield put(deselectAllInputs());
    
    // Refresh the inputs list
    yield call(fetchInputsSaga);
    yield call(fetchInputsStatisticsSaga);
  } catch (error: any) {
    console.error('Error bulk deactivating inputs:', error);
    yield put(setError(error.message || 'Failed to deactivate inputs'));
  } finally {
    yield put(setBulkLoading(false));
  }
}

function* fetchInputsStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(inputsApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching inputs statistics:', error);
    // Don't show error for statistics as it's not critical
  }
}

// Helper saga to auto-fetch input types when needed
function* autoFetchInputTypes(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    if (state.inputs.inputTypes.length === 0) {
      yield call(fetchInputTypesSaga);
    }
  } catch (error) {
    // Ignore errors in auto-fetch
  }
}

// Watcher Sagas
function* watchFetchInputs(): Generator<any, void, any> {
  yield takeLatest(FETCH_INPUTS, fetchInputsSaga);
}

function* watchFetchInputById(): Generator<any, void, any> {
  yield takeEvery(FETCH_INPUT_BY_ID, fetchInputByIdSaga);
}

function* watchCreateInput(): Generator<any, void, any> {
  yield takeEvery(CREATE_INPUT, function*(action: PayloadAction<CreateInputRequest>): Generator<any, void, any> {
    yield call(autoFetchInputTypes);
    yield call(createInputSaga, action);
  });
}

function* watchUpdateInput(): Generator<any, void, any> {
  yield takeEvery(UPDATE_INPUT, function*(action: PayloadAction<UpdateInputRequest>): Generator<any, void, any> {
    yield call(autoFetchInputTypes);
    yield call(updateInputSaga, action);
  });
}

function* watchDeleteInput(): Generator<any, void, any> {
  yield takeEvery(DELETE_INPUT, deleteInputSaga);
}

function* watchFetchInputTypes(): Generator<any, void, any> {
  yield takeLatest(FETCH_INPUT_TYPES, fetchInputTypesSaga);
}

function* watchBulkActivateInputs(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE_INPUTS, bulkActivateInputsSaga);
}

function* watchBulkDeactivateInputs(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE_INPUTS, bulkDeactivateInputsSaga);
}

function* watchFetchInputsStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_INPUTS_STATISTICS, fetchInputsStatisticsSaga);
}

// Root saga
export function* inputsSaga(): Generator<any, void, any> {
  yield all([
    watchFetchInputs(),
    watchFetchInputById(),
    watchCreateInput(),
    watchUpdateInput(),
    watchDeleteInput(),
    watchFetchInputTypes(),
    watchBulkActivateInputs(),
    watchBulkDeactivateInputs(),
    watchFetchInputsStatistics(),
  ]);
} 